/* Depth parallax for the sky-story cloud layers.

   Each layer is displaced by its own distance from the viewport centre times its
   speed. That means a layer sits at exactly its authored position while it is
   centred on screen, and drifts as it enters and leaves.

   The obvious alternative — translateY(scrollY * speed) — was rejected: it pulls
   every layer progressively further from its authored position the deeper you
   scroll, so the composition that was carefully positioned against the design
   only holds at the very top of the page.

   Layers are grouped into two of these elements (behind the text and in front of
   it), and each element is the offsetParent for its own layers. */
if (!customElements.get('sky-parallax')) {
  customElements.define(
    'sky-parallax',
    class SkyParallax extends HTMLElement {
      connectedCallback() {
        this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.layers = Array.from(this.querySelectorAll('[data-parallax]'))
          .map((el) => ({ el, speed: parseFloat(el.dataset.parallax || '0') / 100 }))
          .filter((layer) => layer.speed);
        if (!this.layers.length) return;

        /* Fail open. This is only an optimisation to skip work while the section
           is far off screen, so it must never be the reason the effect does not
           run: if the observer never reports, the effect still works. */
        this.visible = true;
        this.queued = false;
        this.measure = this.measure.bind(this);
        this.request = this.request.bind(this);

        this.measure();

        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries[entries.length - 1].isIntersecting;
            if (this.visible) this.request();
          },
          { rootMargin: '25% 0px' }
        );
        this.observer.observe(this);

        /* The layers size themselves from their PNG's aspect ratio, so their
           heights are 0 until each image loads — well after connectedCallback.
           Watching the layers re-measures at exactly the point their geometry
           becomes real, instead of caching zeroes forever. */
        this.resizeObserver = new ResizeObserver(this.measure);
        this.layers.forEach((layer) => this.resizeObserver.observe(layer.el));

        window.addEventListener('scroll', this.request, { passive: true });
        window.addEventListener('resize', this.measure);
        /* Backstop for images that finish after the observers settle. */
        window.addEventListener('load', this.measure);
        this.reduce.addEventListener('change', this.measure);
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.request);
        window.removeEventListener('resize', this.measure);
        window.removeEventListener('load', this.measure);
        this.reduce.removeEventListener('change', this.measure);
        if (this.observer) this.observer.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      /* Cache untransformed layout boxes. getBoundingClientRect on a layer would
         include the transform applied on the previous frame and compound. */
      measure() {
        this.layers.forEach((layer) => {
          layer.top = layer.el.offsetTop;
          layer.height = layer.el.offsetHeight;
        });
        this.render();
      }

      request() {
        if (this.queued || !this.visible) return;
        this.queued = true;
        requestAnimationFrame(() => {
          this.queued = false;
          this.render();
        });
      }

      render() {
        if (this.reduce.matches) {
          this.layers.forEach((layer) => {
            layer.el.style.transform = '';
          });
          return;
        }
        const top = this.getBoundingClientRect().top;
        const centre = window.innerHeight / 2;
        this.layers.forEach((layer) => {
          /* A layer with no height yet has not loaded its image; leave it alone
             rather than displacing it off a bogus centre. */
          if (!layer.height) return;
          const delta = top + layer.top + layer.height / 2 - centre;
          layer.el.style.transform = `translate3d(0, ${(delta * layer.speed).toFixed(1)}px, 0)`;
        });
      }
    }
  );
}
